import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Brush, Expand, Film, Image as ImageIcon, ImagePlus, Maximize2, Move3d, Package, Palette, Plus, RotateCcw, Smartphone,
    Sparkles, Upload, Video, Wand2, X, ZoomIn, Box, Type,
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { IconTileChoice, SegmentedChoice } from '../../../../components/design-system/common';
import { MediaChip } from '../../../../components/design-system/common/MediaChip';
import { LightboxMedia, MediaLightbox } from '../../../../components/design-system/common/MediaLightbox';

import {
    CAMERA_ANGLES, CAMERA_MOVES, StudioBoard, StudioShot, ShotText, Take3d, isTakeShot, resolveEngine, stillSpecKey,
} from '../studio-contract';
import { aspectValue, cameraLabel, moveLabel } from '../labels';
import { ANGLE_LIST, AnglePreset, EditorTool, angleLabel } from '../edit-actions';
import { CameraGlyph, GLYPH_STYLES, MoveGlyph } from './CameraGlyph';
import { ProductInfo } from './ShotCard';
import { Furniture3DStudioWidget } from '../../content-studio/canvas/Furniture3DStudioWidget';
import { Surface3DStudioWidget } from '../../content-studio/canvas/Surface3DStudioWidget';
import { SURFACE_POSES, captureKey, clipIn3d } from '../surface-shots';
import { StudioPrices, clipPrice, generatedClipSeconds, shotClipPrice } from '../use-studio-api';
import { SELECTED, SELECTED_FRAME } from '../../../../components/design-system/common/selection';
import { Graphic, calloutParts, graphicsOf, nextCalloutNumber } from '../graphics';
import { GraphicsPreview } from './GraphicsPreview';
import { ShotLighting } from './LightingReference';

export type InspectorTab = 'shot' | 'camera' | 'edit' | 'text';

interface ShotInspectorProps {
    board: StudioBoard;
    shot: StudioShot;
    index: number;
    products: Map<string, ProductInfo>;
    prices: StudioPrices | null;
    /** Credits of the photo tools, by recipe slug. */
    editPrices?: Record<string, number>;
    onChange: (patch: Partial<StudioShot>) => void;
    onClose: () => void;
    onPickProducts: () => void;
    onMakeStill: () => void;
    onMakeClip: () => void;
    /** A finished 3D clip filmed for real (tiles shot as objects). */
    onMakeReal?: () => void;
    realPrice?: number | null;
    onUploadFraming: (file: File) => Promise<string | null>;
    /** Upload a 3D studio capture (a frame or a recorded take); resolves to its URL. */
    onCapture3d: (file: File) => Promise<string>;
    onEditTool: (tool: EditorTool) => void;
    onDesign: () => void;
    onAngle: (preset: AnglePreset) => void;
    onUpscale: () => void;
    onUseTake: (stage: 'still' | 'clip', assetUuid: string) => void;
    onPreview: () => void;
}

type Kind = 'rug' | 'surface' | 'furniture';

function kindOf(products: ProductInfo[]): Kind {
    const p = products[0];
    if (!p) return 'furniture';
    if (p.visualizationType === 'surface') return 'surface';
    if (/rug|carpet|kilim|runner|tapis/i.test(`${p.category || ''} ${p.name}`)) return 'rug';
    return 'furniture';
}

// The tab a vendor last used stays open as they move from shot to shot.
let lastTab: InspectorTab = 'shot';

/**
 * One shot, opened: its picture large at the top with every version under it,
 * then four tabs in the order the work happens —
 *   Shot    what is in it and where (product, place, what happens),
 *   Camera  from where, and for a video how the camera moves — an AI move,
 *           or the vendor's own, recorded in 3D or filmed with their phone,
 *   Edit    the photo tools, each a tile saying what it does and costs,
 *   Text    words on screen and labels on the photo.
 * The footer always holds the next step and its price.
 */
export const ShotInspector: React.FC<ShotInspectorProps> = (props) => {
    const {
        board, shot, index, products, prices, editPrices = {}, onChange, onClose, onMakeStill, onMakeClip, onMakeReal, realPrice, onEditTool, onDesign, onAngle, onUpscale, onUseTake, onPreview,
    } = props;
    const { t } = useTranslation();
    const [tab, setTabState] = useState<InspectorTab>(lastTab);
    const setTab = (v: InspectorTab) => { lastTab = v; setTabState(v); };
    const [lightbox, setLightbox] = useState<LightboxMedia | null>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    useEffect(() => { scrollRef.current?.scrollTo({ top: 0 }); }, [shot.id]);

    const shotProducts = shot.productUuids.map((u) => products.get(u)).filter(Boolean) as ProductInfo[];
    const stillReady = shot.still?.status === 'ready' && !!shot.still.url;
    const clipReady = shot.clip?.status === 'ready' && !!shot.clip.url;
    const stillPending = shot.still?.status === 'pending' || shot.still?.status === 'waiting';
    const clipPending = shot.clip?.status === 'pending' || (shot.clip?.status === 'waiting');
    const stale = stillReady && shot.still?.specKey && shot.still.specKey !== stillSpecKey(board, shot);
    const take = isTakeShot(shot);
    const filmPrice = shotClipPrice(prices, shot);
    const versions = (shot.still?.takes?.length || 0) + (shot.clip?.takes?.length || 0);

    const tabs: Array<{ id: InspectorTab; label: string }> = [
        { id: 'shot', label: t('STUDIO.INSPECTOR.TAB_SHOT', 'Shot') },
        { id: 'camera', label: t('STUDIO.INSPECTOR.TAB_CAMERA', 'Camera') },
        { id: 'edit', label: t('STUDIO.INSPECTOR.TAB_EDIT', 'Edit') },
        { id: 'text', label: t('STUDIO.INSPECTOR.TAB_TEXT', 'Text') },
    ];

    return (
        <div className="flex h-full flex-col">
            {/* ── header ──────────────────────────── */}
            <div className="flex h-14 shrink-0 items-center gap-1.5 border-b px-3">
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-[11px] font-medium text-foreground">{index + 1}</span>
                <Input
                    value={shot.title || ''}
                    placeholder={cameraLabel(t, shot.camera).label}
                    onChange={(e) => onChange({ title: e.target.value })}
                    aria-label={t('STUDIO.INSPECTOR.TITLE', 'Shot title')}
                    className="h-8 flex-1 border-transparent px-1.5 text-sm font-medium shadow-none hover:border-input focus-visible:border-input"
                />
                <Button variant="ghost" size="icon-sm" onClick={onClose} aria-label={t('STUDIO.CLOSE', 'Close')}><X className="size-4" /></Button>
            </div>

            <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
                {/* ── the picture, large ───────────── */}
                <div className="px-4 pt-4">
                    <div className="relative mx-auto overflow-hidden rounded-xl border bg-muted" style={{ aspectRatio: String(aspectValue(board.format)), maxHeight: 300, maxWidth: board.format === '16:9' ? undefined : `${Math.round(300 * aspectValue(board.format))}px` }}>
                        {shot.kind === 'card' ? (
                            <div className="absolute inset-0" style={{ background: shot.background || '#0B0B0B' }}>
                                <GraphicsPreview graphics={graphicsOf(shot)} />
                            </div>
                        ) : clipReady ? (
                            <video src={shot.clip!.url} poster={shot.still?.url} muted loop autoPlay playsInline className="absolute inset-0 size-full object-cover" />
                        ) : shot.still?.url ? (
                            <img src={shot.still.url} alt="" className={cn('absolute inset-0 size-full object-cover', stillPending && 'opacity-40')} />
                        ) : take ? (
                            <video src={shot.take3d!.url} poster={shot.take3d!.posterUrl} muted loop autoPlay playsInline className="absolute inset-0 size-full object-cover" />
                        ) : (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center text-xs text-muted-foreground">
                                {shotProducts[0]?.thumbnailUrl
                                    ? <img src={shotProducts[0].thumbnailUrl} alt="" className="size-14 rounded-lg object-cover opacity-80" />
                                    : <ImageIcon className="size-5" />}
                                {t('STUDIO.INSPECTOR.NO_PHOTO', 'No photo yet')}
                            </div>
                        )}
                        {(stillPending || clipPending) && (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background/40 backdrop-blur-[2px]">
                                <Spinner className="size-5" />
                                <span className="rounded-md bg-background/90 px-2 py-0.5 text-xs text-foreground">
                                    {clipPending ? t('STUDIO.SHOT.FILMING', 'Filming…') : t('STUDIO.SHOT.SHOOTING', 'Taking the photo…')}
                                </span>
                            </div>
                        )}
                        {(stillReady || clipReady) && (
                            <button type="button" onClick={onPreview} aria-label={t('STUDIO.PREVIEW.OPEN', 'View full size')} title={t('STUDIO.PREVIEW.OPEN', 'View full size')}
                                className="absolute end-2 top-2 flex size-7 cursor-pointer items-center justify-center rounded-full bg-black/45 text-white backdrop-blur-md transition-colors hover:bg-black/60">
                                <Maximize2 className="size-3.5" />
                            </button>
                        )}
                    </div>
                    {versions > 0 && <Versions shot={shot} onUseTake={onUseTake} />}
                    {stale && (
                        <p className="mt-2 rounded-lg bg-accent/60 px-2.5 py-1.5 text-xs text-muted-foreground">{t('STUDIO.SHOT.STALE', 'You changed this shot since its photo — make a new take to see it.')}</p>
                    )}
                </div>

                {/* ── tabs ──────────────────────────── */}
                <div className="sticky top-0 z-10 bg-background/95 px-4 pb-3 pt-4 backdrop-blur">
                    <div role="tablist" className="grid grid-cols-4 gap-1 rounded-xl bg-muted/70 p-1">
                        {tabs.map((x) => (
                            <button
                                key={x.id}
                                type="button"
                                role="tab"
                                data-insp-tab={x.id}
                                aria-selected={tab === x.id}
                                onClick={() => setTab(x.id)}
                                className={cn(
                                    'h-7 cursor-pointer rounded-lg text-[13px] transition-colors',
                                    tab === x.id ? 'bg-background font-medium text-foreground shadow-[0_1px_2px_rgba(28,25,23,0.08)]' : 'text-muted-foreground hover:text-foreground',
                                )}
                            >
                                {x.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div key={tab} className="space-y-6 px-4 pb-6 pt-1 animate-in fade-in duration-200">
                    {tab === 'shot' && (
                        <ShotTab {...props} kind={kindOf(shotProducts)} shotProducts={shotProducts} onLightbox={setLightbox} />
                    )}
                    {tab === 'camera' && (
                        <CameraTab {...props} kind={kindOf(shotProducts)} hero={shotProducts[0]} />
                    )}
                    {tab === 'edit' && (
                        <EditTab stillReady={stillReady} busy={stillPending} editPrices={editPrices} onDesign={onDesign} onEditTool={onEditTool} onAngle={onAngle} onUpscale={onUpscale} />
                    )}
                    {tab === 'text' && <TextTab shot={shot} onChange={onChange} />}
                </div>
            </div>

            {/* ── the next step, with its price ──────── */}
            {shot.kind !== 'card' && <div className="shrink-0 space-y-2 border-t bg-background px-4 py-3">
                <div className="flex gap-2">
                    {!(take && !stillReady) && (
                        <Button variant="outline" className="flex-1 gap-1.5" onClick={onMakeStill} disabled={stillPending || !shot.productUuids.length}>
                            <ImagePlus className="size-4" />
                            {stillReady ? t('STUDIO.SHOT.NEW_TAKE', 'New photo take') : t('STUDIO.SHOT.MAKE_PHOTO', 'Make photo')}
                            {prices && <span className="text-muted-foreground">· {prices.still}</span>}
                        </Button>
                    )}
                    {shot.kind === 'video' && (
                        <Button variant="outline" className={cn('flex-1 gap-1.5', (take || stillReady) && !clipReady && 'border-foreground/30')} onClick={onMakeClip}
                            disabled={clipPending || (take ? false : !stillReady)}>
                            {take ? <Sparkles className="size-4" /> : <Film className="size-4" />}
                            {take ? t('STUDIO.TAKE.MAKE_REAL', 'Make it real') : clipReady ? t('STUDIO.SHOT.NEW_CLIP', 'Film it again') : t('STUDIO.SHOT.ANIMATE', 'Film it')}
                            <span className="text-muted-foreground">· {filmPrice ?? '—'}</span>
                        </Button>
                    )}
                </div>
                {onMakeReal && clipIn3d(shot) && clipReady && !take && (
                    <Button variant="outline" className="w-full gap-1.5" onClick={onMakeReal} disabled={clipPending}>
                        <Sparkles className="size-4" />
                        {t('STUDIO.TAKE.MAKE_REAL', 'Make it real')}
                        <span className="text-muted-foreground">· {realPrice ?? '—'}</span>
                    </Button>
                )}
                {shot.kind === 'video' && !take && !stillReady && (
                    <p className="flex items-center gap-1 text-xs text-muted-foreground"><Sparkles className="size-3" />{t('STUDIO.SHOT.PHOTO_FIRST', 'Approve the photo first — the film starts from it.')}</p>
                )}
            </div>}
            <MediaLightbox media={lightbox} onClose={() => setLightbox(null)} />
        </div>
    );
};

// ── Shot ────────────────────────────────────────────────────────────

const ShotTab: React.FC<ShotInspectorProps & { kind: Kind; shotProducts: ProductInfo[]; onLightbox: (m: LightboxMedia) => void }> = ({
    board, shot, kind, shotProducts, onChange, onPickProducts, onCapture3d, onLightbox,
}) => {
    const { t } = useTranslation();
    const hero = shotProducts[0];
    const sceneIdeas: Record<Kind, string[]> = {
        rug: [
            t('STUDIO.IDEA.SCENE_RUG_1', 'Sunlit living room, pale oak floor, linen sofa behind'),
            t('STUDIO.IDEA.SCENE_RUG_2', 'Quiet bedroom at morning, white sheets, soft window light'),
            t('STUDIO.IDEA.SCENE_RUG_3', 'Long hallway runner, warm wooden floorboards'),
            t('STUDIO.IDEA.SCENE_RUG_4', 'Calm reading corner, a boucle armchair, a stack of books'),
        ],
        surface: [
            t('STUDIO.IDEA.SCENE_SURF_1', 'Bright bathroom with a freestanding tub and a brass tap'),
            t('STUDIO.IDEA.SCENE_SURF_2', 'Open kitchen, the floor running under a long island'),
            t('STUDIO.IDEA.SCENE_SURF_3', 'Haussmann living room with tall windows and mouldings'),
            t('STUDIO.IDEA.SCENE_SURF_4', 'Sunny terrace by a pool, Mediterranean light'),
        ],
        furniture: [
            t('STUDIO.IDEA.SCENE_FURN_1', 'Warm living room at golden hour, plaster walls'),
            t('STUDIO.IDEA.SCENE_FURN_2', 'Minimal loft, concrete floor, one big plant'),
            t('STUDIO.IDEA.SCENE_FURN_3', 'Terrace under a pergola, dappled sunlight'),
            t('STUDIO.IDEA.SCENE_FURN_4', 'Scandinavian dining room, oak table, morning light'),
        ],
    };
    const actionIdeas: Record<Kind, string[]> = {
        rug: [
            t('STUDIO.IDEA.ACT_RUG_1', 'Bare feet step onto it, legs only from the knees down'),
            t('STUDIO.IDEA.ACT_RUG_2', 'A hand slowly brushes across the pile'),
            t('STUDIO.IDEA.ACT_RUG_3', 'Someone in wide jeans walks across it, legs only'),
            t('STUDIO.IDEA.ACT_RUG_4', 'A dog naps on it in a patch of sun'),
        ],
        surface: [
            t('STUDIO.IDEA.ACT_SURF_1', 'Sunlight slowly moves across the floor'),
            t('STUDIO.IDEA.ACT_SURF_2', 'Bare feet walk across the tiles'),
            t('STUDIO.IDEA.ACT_SURF_3', 'Steam rises from the bath, soft morning light'),
        ],
        furniture: [
            t('STUDIO.IDEA.ACT_FURN_1', 'Someone sits down and leans back, seen from the side'),
            t('STUDIO.IDEA.ACT_FURN_2', 'A hand runs along the armrest'),
            t('STUDIO.IDEA.ACT_FURN_3', 'A cup of coffee is set down beside it'),
            t('STUDIO.IDEA.ACT_FURN_4', 'Curtains move gently in the breeze behind it'),
        ],
    };

    return (
        <>
            <Field label={t('STUDIO.SHOT.KIND', 'This shot is a')}>
                <SegmentedChoice
                    value={shot.kind}
                    onChange={(kindValue) => onChange({
                        kind: kindValue,
                        ...(kindValue === 'video' && !shot.motion ? { motion: 'push-in', durationSec: shot.durationSec || 4 } : {}),
                        ...(kindValue === 'card' ? { background: shot.background || '#0B0B0B', durationSec: shot.durationSec || 2 } : {}),
                    })}
                    options={[
                        { value: 'image', icon: <ImageIcon className="size-4" />, label: t('STUDIO.SHOT.PHOTO', 'Photo'), hint: t('STUDIO.SHOT.PHOTO_HINT', 'A still picture for a post or a carousel') },
                        { value: 'video', icon: <Video className="size-4" />, label: t('STUDIO.SHOT.VIDEO', 'Video'), hint: t('STUDIO.SHOT.VIDEO_HINT', 'A photo first, then filmed into a short clip') },
                        { value: 'card', icon: <Type className="size-4" />, label: t('STUDIO.SHOT.CARD', 'Card'), hint: t('STUDIO.SHOT.CARD_KIND_HINT', 'A plain colour with your words and logo — a title or end card. Free.') },
                    ]}
                />
            </Field>

            {shot.kind === 'card' && (
                <Field label={t('STUDIO.SHOT.CARD_COLOR', 'Card colour')}>
                    <div className="flex items-center gap-2">
                        {['#0B0B0B', '#FFFFFF', '#F3EFE8', '#1F2A24', '#2B2F3A'].map((c) => (
                            <button key={c} type="button" onClick={() => onChange({ background: c })} aria-label={c}
                                className={cn('size-8 cursor-pointer rounded-lg border transition-shadow', (shot.background || '#0B0B0B').toLowerCase() === c.toLowerCase() && 'ring-[3px] ring-foreground/15')}
                                style={{ background: c }} />
                        ))}
                        <input type="color" value={/^#[0-9a-f]{6}$/i.test(shot.background || '') ? shot.background : '#0B0B0B'} onChange={(e) => onChange({ background: e.target.value })}
                            aria-label={t('STUDIO.SHOT.CARD_COLOR', 'Card colour')} className="size-8 cursor-pointer rounded-lg border bg-transparent p-0.5" />
                    </div>
                    <div className="mt-3">
                        <div className="mb-1.5 text-xs text-muted-foreground">{t('STUDIO.SHOT.LENGTH', 'Length on screen')}</div>
                        <SegmentedChoice value={Math.round(shot.durationSec || 2)} onChange={(durationSec) => onChange({ durationSec })} options={[1, 2, 3, 4].map((n) => ({ value: n, label: `${n}s` }))} />
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">{t('STUDIO.SHOT.CARD_WORDS_HINT', 'Its words and logo are in the Text tab.')}</p>
                </Field>
            )}
            {shot.kind !== 'card' && <>

            <Field label={t('STUDIO.SHOT.PRODUCTS', 'Product')}>
                <div className="flex flex-wrap gap-1.5">
                    {shotProducts.map((p) => (
                        <MediaChip
                            key={p.uuid}
                            label={p.name}
                            thumbnailUrl={p.thumbnailUrl}
                            icon={<Package />}
                            onOpen={p.imageUrl || p.thumbnailUrl ? () => onLightbox({ url: (p.imageUrl || p.thumbnailUrl)!, kind: 'image', title: p.name }) : undefined}
                            openLabel={t('STUDIO.OPEN_PREVIEW', 'Preview')}
                            onRemove={() => onChange({ productUuids: shot.productUuids.filter((u) => u !== p.uuid) })}
                            removeLabel={t('STUDIO.REMOVE', 'Remove')}
                        />
                    ))}
                    <Button variant="outline" size="sm" onClick={onPickProducts} className="h-8 gap-1"><Plus className="size-3.5" />{shotProducts.length ? t('STUDIO.SHOT.ADD_PRODUCT', 'Add') : t('STUDIO.SHOT.PICK_PRODUCT', 'Pick a product')}</Button>
                </div>
            </Field>

            <Field label={t('STUDIO.SHOT.SCENE', 'Where')}>
                <Textarea
                    value={shot.scene}
                    rows={3}
                    placeholder={t('STUDIO.SHOT.SCENE_PH', 'The room or place, the light, the mood…')}
                    onChange={(e) => onChange({ scene: e.target.value })}
                    className="min-h-[76px] resize-none text-sm"
                />
                <Ideas ideas={sceneIdeas[kind]} onPick={(v) => onChange({ scene: v })} />
            </Field>

            {shot.surfaceUse !== 'object' && (
                <Field label={t('STUDIO.LIGHT.SHOT_LABEL', 'Lighting reference')} optional>
                    <ShotLighting value={shot.lookRef} film={board.lookRef} onChange={(lookRef) => onChange({ lookRef })} />
                </Field>
            )}

            {hero?.visualizationType === 'surface' && (
                <Field label={t('STUDIO.SHOT.SURFACE_USE', 'How the product appears')}>
                    <SegmentedChoice
                        value={shot.surfaceUse === 'object' ? 'object' : 'installed'}
                        onChange={(surfaceUse) => onChange({ surfaceUse, pose: surfaceUse === 'object' ? (shot.pose || 'single') : shot.pose })}
                        options={[
                            { value: 'installed', label: t('STUDIO.SHOT.SURFACE_INSTALLED', 'Laid in a room'), hint: t('STUDIO.SHOT.SURFACE_INSTALLED_HINT', 'Your real texture laid on the floor or wall of the scene, at its true size.') },
                            { value: 'object', label: t('STUDIO.SHOT.SURFACE_OBJECT', 'The tile itself'), hint: t('STUDIO.SHOT.SURFACE_OBJECT_HINT', 'A packshot, a sample board, a flat lay or a close-up — the real pieces built in 3D.') },
                        ]}
                    />
                    {shot.surfaceUse !== 'object' && (
                        <div className="mt-2">
                            <SegmentedChoice
                                value={shot.surfaceOn || 'auto'}
                                onChange={(v) => onChange({ surfaceOn: v === 'auto' ? undefined : v })}
                                options={[
                                    { value: 'auto', label: t('STUDIO.SHOT.SURFACE_ON_AUTO', 'Auto'), hint: t('STUDIO.SHOT.SURFACE_ON_AUTO_HINT', 'Laid where the scene puts it — the floor unless it says a wall.') },
                                    { value: 'floor', label: t('STUDIO.SHOT.SURFACE_ON_FLOOR', 'Floor'), hint: t('STUDIO.SHOT.SURFACE_ON_FLOOR_HINT', 'Your tile is laid on the floor of the scene.') },
                                    { value: 'walls', label: t('STUDIO.SHOT.SURFACE_ON_WALLS', 'Wall'), hint: t('STUDIO.SHOT.SURFACE_ON_WALLS_HINT', 'Your tile covers the walls of the scene.') },
                                ]}
                            />
                        </div>
                    )}
                    {shot.surfaceUse === 'object' && (
                        <>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                                {SURFACE_POSES.map((p) => (
                                    <button key={p} type="button" onClick={() => onChange({ pose: p })}
                                        className={cn('h-7 cursor-pointer rounded-lg border px-2.5 text-xs transition-colors hover:bg-accent', (shot.pose || 'single') === p ? `${SELECTED} text-foreground` : 'border-input text-muted-foreground')}>
                                        {t(`STUDIO.POSE.${p.replace('-', '_').toUpperCase()}`, p)}
                                    </button>
                                ))}
                            </div>
                            <ToolCard
                                className="mt-2"
                                icon={<Box />}
                                title={t('STUDIO.SHOT.POSE_3D', 'Pose it in 3D')}
                                hint={t('STUDIO.SHOT.POSE_3D_SURFACE_HINT', 'Studio poses the real tiles for you when the photo is made. Open it to frame them yourself.')}
                                aside={(
                                    <div className="w-24 shrink-0">
                                        <Surface3DStudioWidget
                                            productUuid={hero.uuid}
                                            applicationTypes={['floor', 'walls']}
                                            mode="single"
                                            lockMode
                                            poseId={shot.pose || 'single'}
                                            aspectRatio={board.format}
                                            snapshotUrl={shot.framingRef?.source === 'studio3d' ? shot.framingRef.url : undefined}
                                            onCapture={onCapture3d}
                                            onSnapshotUpdated={(url) => onChange({ framingRef: { url, source: 'studio3d', key: captureKey(board, shot) } })}
                                        />
                                    </div>
                                )}
                            />
                        </>
                    )}
                </Field>
            )}

            <Field label={t('STUDIO.SHOT.ACTION', 'What happens')} optional>
                <Textarea
                    value={shot.action || ''}
                    rows={2}
                    placeholder={t('STUDIO.SHOT.ACTION_PH', 'People, hands, pets, light — or leave empty')}
                    onChange={(e) => onChange({ action: e.target.value || undefined })}
                    className="min-h-[56px] resize-none text-sm"
                />
                <Ideas ideas={actionIdeas[kind]} onPick={(v) => onChange({ action: v })} />
            </Field>
            </>}
        </>
    );
};

// ── Camera ──────────────────────────────────────────────────────────

const CameraTab: React.FC<ShotInspectorProps & { kind: Kind; hero?: ProductInfo }> = ({
    board, shot, kind, hero, prices, onChange, onUploadFraming, onCapture3d,
}) => {
    const { t } = useTranslation();
    const fileRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const frames = board.reference?.frames || [];
    const engine = resolveEngine(shot);
    const seconds = generatedClipSeconds(shot);
    // Tiles as objects are real slabs in the surface studio, so a camera can
    // move around them; tiles laid in a room have no 3D subject to film.
    const surfaceTake = hero?.visualizationType === 'surface' && shot.surfaceUse === 'object';
    const canTake = !!hero?.modelPath || surfaceTake;
    const ownMove = shot.engine === 'take';

    const setTake = (take: Take3d) => onChange({ take3d: take, engine: 'take', durationSec: Math.max(1, Math.round(take.durationSec * 10) / 10) });

    /** The 3D studio opened on its Video panel (keyframes) or its Phone panel. */
    const takeStudio = (dock: 'video' | 'phone', trigger: (open: () => void) => React.ReactNode) => (surfaceTake ? (
        <Surface3DStudioWidget
            productUuid={hero.uuid}
            applicationTypes={['floor', 'walls']}
            mode="single"
            lockMode
            poseId={shot.pose || 'single'}
            aspectRatio={board.format}
            initialDock={dock}
            onCapture={onCapture3d}
            onSnapshotUpdated={() => undefined}
            onTakeRecorded={(take) => setTake({ ...take, source: dock === 'phone' ? 'phone' : 'keyframes' })}
            renderTrigger={trigger}
        />
    ) : hero?.modelPath && (
        <Furniture3DStudioWidget
            modelPath={hero.modelPath}
            productUuid={hero.uuid}
            aspectRatio={board.format}
            isRug={kind === 'rug'}
            allowRoomMode
            initialDock={dock}
            onCapture={onCapture3d}
            onSnapshotUpdated={() => undefined}
            onTakeRecorded={(take) => setTake({ ...take, source: dock === 'phone' ? 'phone' : 'keyframes' })}
            renderTrigger={trigger}
        />
    ));

    return (
        <>
            <Field label={t('STUDIO.SHOT.CAMERA', 'Camera')}>
                {shot.framingRef ? (
                    <div className="flex items-center gap-3 rounded-xl border p-2">
                        <img src={shot.framingRef.url} alt="" className="h-16 w-12 rounded-md object-cover" />
                        <div className="min-w-0 flex-1">
                            <div className="text-sm text-foreground">{t('STUDIO.SHOT.FRAMING_ON', 'Copying this framing exactly')}</div>
                            <div className="text-xs text-muted-foreground">{t('STUDIO.SHOT.FRAMING_ON_HINT', 'Same camera height, angle and crop — with your product.')}</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => onChange({ framingRef: undefined })}>{t('STUDIO.REMOVE', 'Remove')}</Button>
                    </div>
                ) : (
                    <>
                        <IconTileChoice
                            value={shot.camera}
                            onChange={(camera) => onChange({ camera })}
                            options={CAMERA_ANGLES.map((c) => ({ value: c, ...cameraLabel(t, c), icon: <CameraGlyph angle={c} className="h-full w-auto" /> }))}
                        />
                        <Input
                            value={shot.cameraNote || ''}
                            onChange={(e) => onChange({ cameraNote: e.target.value })}
                            placeholder={t('STUDIO.SHOT.CAMERA_NOTE_PH', 'Anything precise? e.g. "the fringe fills the bottom of the frame"')}
                            className="mt-2 h-9 text-sm"
                        />
                    </>
                )}
            </Field>

            <Field label={t('STUDIO.SHOT.FRAME_IT', 'Frame it exactly')} optional>
                <div className="space-y-2">
                    {hero?.modelPath && (
                        <Furniture3DStudioWidget
                            modelPath={hero.modelPath}
                            productUuid={hero.uuid}
                            snapshotUrl={shot.framingRef?.source === 'studio3d' ? shot.framingRef.url : undefined}
                            aspectRatio={board.format}
                            isRug={kind === 'rug'}
                            onCapture={onCapture3d}
                            onSnapshotUpdated={(url) => onChange({ framingRef: { url, source: 'studio3d' } })}
                            renderTrigger={(open) => (
                                <ToolCard as="button" onClick={open} icon={<Box />}
                                    title={t('STUDIO.SHOT.POSE_3D', 'Pose it in 3D')}
                                    hint={t('STUDIO.SHOT.POSE_3D_HINT', 'Turn the real 3D model and frame it exactly as you want — the photo keeps that angle and crop.')} />
                            )}
                        />
                    )}
                    {frames.length > 0 && !shot.framingRef && (
                        <div>
                            <div className="mb-1.5 text-xs text-muted-foreground">{t('STUDIO.SHOT.COPY_FRAME', 'Or copy the exact framing of a reference shot')}</div>
                            <div className="flex gap-1.5 overflow-x-auto pb-1">
                                {frames.map((f, i) => (
                                    <button key={f.url} type="button" onClick={() => onChange({ framingRef: { url: f.url, source: 'reference' } })}
                                        className="relative shrink-0 cursor-pointer overflow-hidden rounded-lg border transition-colors hover:border-foreground/40">
                                        <img src={f.url} alt="" className="h-20 w-auto object-cover" />
                                        <span className="absolute bottom-1 start-1 flex size-4 items-center justify-center rounded-full bg-black/50 text-[9px] text-white">{i + 1}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                    {!shot.framingRef && (
                        <>
                            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={async (e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                if (!file) return;
                                setUploading(true);
                                const url = await onUploadFraming(file);
                                setUploading(false);
                                if (url) onChange({ framingRef: { url, source: 'upload' } });
                            }} />
                            <ToolCard as="button" onClick={() => fileRef.current?.click()} icon={uploading ? <Spinner className="size-4" /> : <Upload />}
                                title={uploading ? t('STUDIO.UPLOADING', 'Uploading…') : t('STUDIO.SHOT.UPLOAD_FRAME_SHORT', 'Copy a picture’s framing')}
                                hint={t('STUDIO.SHOT.UPLOAD_FRAME_HINT', 'Upload a photo you like — the shot takes its camera height, angle and crop.')} />
                        </>
                    )}
                </div>
            </Field>

            {shot.kind === 'video' && (
                <Field label={t('STUDIO.SHOT.MOTION', 'Camera move')}>
                    {canTake && (
                        <div className="mb-3">
                            <SegmentedChoice
                                value={ownMove ? 'take' : 'ai'}
                                onChange={(v) => onChange(v === 'take' ? { engine: 'take' } : { engine: undefined })}
                                options={[
                                    { value: 'ai', icon: <Sparkles className="size-4" />, label: t('STUDIO.TAKE.AI_MOVE', 'AI camera move'), hint: t('STUDIO.TAKE.AI_MOVE_HINT', 'Pick a move; the photo is filmed into a clip.') },
                                    { value: 'take', icon: <Move3d className="size-4" />, label: t('STUDIO.TAKE.OWN_MOVE', 'Your own move'), hint: t('STUDIO.TAKE.OWN_MOVE_HINT', 'Direct the camera yourself in 3D or with your phone — Ardi turns it into a real film.') },
                                ]}
                            />
                        </div>
                    )}
                    {ownMove && canTake ? (
                        shot.take3d?.url ? (
                            <div className="overflow-hidden rounded-xl border">
                                <div className="relative bg-neutral-900" style={{ aspectRatio: String(aspectValue(board.format)), maxHeight: 220 }}>
                                    <video src={shot.take3d.url} poster={shot.take3d.posterUrl} muted loop autoPlay playsInline className="absolute inset-0 size-full object-contain" />
                                    <span className="absolute start-2 top-2 inline-flex h-5 items-center gap-1 rounded-full bg-black/45 px-2 text-[10.5px] text-white backdrop-blur-md">
                                        {shot.take3d.source === 'phone' ? <Smartphone className="size-3" /> : <Move3d className="size-3" />}
                                        {`${Math.round(shot.take3d.durationSec * 10) / 10}s`}
                                    </span>
                                </div>
                                <div className="flex items-center gap-2 p-2.5">
                                    <div className="min-w-0 flex-1 text-xs text-muted-foreground">
                                        {t('STUDIO.TAKE.READY', 'Your camera move. “Make it real” films it for real, in the scene you described.')}
                                    </div>
                                    {takeStudio(shot.take3d.source === 'phone' ? 'phone' : 'video', (open) => (
                                        <Button variant="outline" size="sm" className="gap-1.5" onClick={open}><RotateCcw className="size-3.5" />{t('STUDIO.TAKE.REDO', 'Record again')}</Button>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div className="grid grid-cols-2 gap-2">
                                {takeStudio('video', (open) => (
                                    <TakeTile onClick={open} icon={<Move3d />} title={t('STUDIO.TAKE.KEYFRAMES', 'Set keyframes')} hint={t('STUDIO.TAKE.KEYFRAMES_HINT', 'Frame a few views in 3D; the camera flies between them.')} />
                                ))}
                                {takeStudio('phone', (open) => (
                                    <TakeTile onClick={open} icon={<Smartphone />} title={t('STUDIO.TAKE.PHONE', 'Film with your phone')} hint={surfaceTake
                                        ? t('STUDIO.TAKE.PHONE_HINT_SURFACE', 'Scan a code and walk around the tiles with your phone.')
                                        : t('STUDIO.TAKE.PHONE_HINT', 'Scan a code and walk around the product with your phone.')} />
                                ))}
                            </div>
                        )
                    ) : (
                        <>
                            <style>{GLYPH_STYLES}</style>
                            <IconTileChoice
                                value={shot.motion || 'push-in'}
                                onChange={(motion) => onChange({ motion })}
                                tileClassName="glyph-tile"
                                options={CAMERA_MOVES.map((m) => ({ value: m, ...moveLabel(t, m), icon: <MoveGlyph move={m} className="h-full w-auto" /> }))}
                            />
                            <div className="mt-4 space-y-3">
                                <div>
                                    <div className="mb-1.5 text-xs text-muted-foreground">{t('STUDIO.SHOT.LENGTH', 'Length on screen')}</div>
                                    <SegmentedChoice
                                        value={Math.round(shot.durationSec || 4)}
                                        onChange={(durationSec) => onChange({ durationSec })}
                                        options={[2, 3, 4, 6, 8].map((n) => ({ value: n, label: `${n}s` }))}
                                    />
                                </div>
                                <div>
                                    <div className="mb-1.5 text-xs text-muted-foreground">{t('STUDIO.SHOT.QUALITY', 'Quality')}</div>
                                    <SegmentedChoice
                                        value={engine}
                                        onChange={(value) => onChange({ engine: value as any })}
                                        options={[
                                            { value: 'fast', label: `${t('STUDIO.SHOT.FAST', 'Fast')} · ${clipPrice(prices, 'fast', seconds) ?? '—'}`, hint: t('STUDIO.SHOT.FAST_HINT', 'Ready in under a minute. Great for simple camera moves.') },
                                            { value: 'cinematic', label: `${t('STUDIO.SHOT.CINEMATIC', 'Cinematic')} · ${clipPrice(prices, 'cinematic', seconds) ?? '—'}`, hint: t('STUDIO.SHOT.CINEMATIC_HINT', 'Best realism and product fidelity, people move naturally. A few minutes.') },
                                        ]}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </Field>
            )}
        </>
    );
};

// ── Edit ────────────────────────────────────────────────────────────

const EditTab: React.FC<{
    stillReady: boolean;
    busy: boolean;
    editPrices: Record<string, number>;
    onDesign: () => void;
    onEditTool: (tool: EditorTool) => void;
    onAngle: (p: AnglePreset) => void;
    onUpscale: () => void;
}> = ({ stillReady, busy, editPrices, onDesign, onEditTool, onAngle, onUpscale }) => {
    const { t } = useTranslation();
    if (!stillReady) {
        return (
            <div className="rounded-xl border border-dashed px-4 py-8 text-center">
                <Wand2 className="mx-auto mb-2 size-5 text-muted-foreground" />
                <div className="text-sm text-foreground">{busy ? t('STUDIO.EDIT.WAIT', 'The photo is on its way') : t('STUDIO.EDIT.NO_PHOTO', 'Make the photo first')}</div>
                <p className="mt-1 text-xs text-muted-foreground">{t('STUDIO.EDIT.NO_PHOTO_HINT', 'Then retouch it, change it with words, widen it, turn the camera or design it here.')}</p>
            </div>
        );
    }
    const price = (slug: string) => (editPrices[slug] != null ? editPrices[slug] : undefined);
    return (
        <>
            <ToolCard
                as="button"
                onClick={onDesign}
                icon={<Palette />}
                title={t('STUDIO.EDIT.DESIGN_TITLE', 'Design')}
                hint={t('STUDIO.EDIT.DESIGN_HINT', 'Open the photo in the design editor: layers, text, your logo, shapes and templates')}
                badge={t('STUDIO.EDIT.FREE', 'Free')}
                large
            />
            <div>
                <div className="mb-2 text-xs font-medium text-muted-foreground">{t('STUDIO.EDIT.WITH_AI', 'Change it with AI')}</div>
                <div className="grid grid-cols-2 gap-2">
                    <ToolTile icon={<Brush />} title={t('STUDIO.EDIT.RETOUCH', 'Retouch an area')} hint={t('STUDIO.EDIT.RETOUCH_HINT', 'Circle something and say what to do with it')} price={price('retouch-image')} onClick={() => onEditTool('retouch')} />
                    <ToolTile icon={<Wand2 />} title={t('STUDIO.EDIT.MODIFY', 'Change with a prompt')} hint={t('STUDIO.EDIT.MODIFY_HINT', '“Make it evening”, “add a plant on the left”')} price={price('modify-image')} onClick={() => onEditTool('modify')} />
                    <ToolTile icon={<Expand />} title={t('STUDIO.EDIT.EXPAND', 'Expand the frame')} hint={t('STUDIO.EDIT.EXPAND_HINT', 'Show more around the picture')} price={price('expand-image')} onClick={() => onEditTool('expand')} />
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <ToolTile icon={<Move3d />} title={t('STUDIO.EDIT.ANGLE', 'Change angle')} hint={t('STUDIO.EDIT.ANGLE_HINT', 'The same scene, seen from elsewhere')} price={price('style-variation')} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-52">
                            {ANGLE_LIST.map((p) => <DropdownMenuItem key={p} onSelect={() => onAngle(p)}>{angleLabel(t, p)}</DropdownMenuItem>)}
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <ToolTile icon={<ZoomIn />} title={t('STUDIO.EDIT.UPSCALE_SHORT', 'Enlarge 2×')} hint={t('STUDIO.EDIT.UPSCALE_HINT', 'Sharper, twice the size, for print and web')} price={price('upscale-image')} onClick={onUpscale} />
                </div>
                <p className="mt-2 text-[11px] text-muted-foreground">{t('STUDIO.EDIT.KEEPS', 'Every edit is a new version — the one before stays above, one click away.')}</p>
            </div>
        </>
    );
};

// ── Text ────────────────────────────────────────────────────────────

const TextTab: React.FC<{ shot: StudioShot; onChange: (p: Partial<StudioShot>) => void }> = ({ shot, onChange }) => {
    const { t } = useTranslation();
    const graphics = graphicsOf(shot);
    const setGraphics = (next: Graphic[]) => onChange({ graphics: next.length ? next : undefined, callouts: undefined });
    const setText = (patch: Partial<ShotText>) => {
        const base: ShotText = shot.text || { content: '', role: 'title', font: 'serif' };
        const next = { ...base, ...patch };
        onChange({ text: next.content ? next : undefined });
    };
    return (
        <>
            <Field label={t('STUDIO.SHOT.TEXT', 'Text on screen')} optional>
                <Textarea
                    value={shot.text?.content || ''}
                    rows={2}
                    onChange={(e) => setText({ content: e.target.value })}
                    placeholder={t('STUDIO.SHOT.TEXT_PH', 'e.g. "Monday", "quiet", "Our softest rug yet"')}
                    className="min-h-[56px] resize-none text-sm"
                />
                {shot.text?.content && (
                    <div className="mt-2 space-y-2">
                        <SegmentedChoice
                            value={shot.text.role}
                            onChange={(role) => setText({ role })}
                            options={[
                                { value: 'title', label: t('STUDIO.TEXT.TITLE', 'Title') },
                                { value: 'caption', label: t('STUDIO.TEXT.CAPTION', 'Line') },
                                { value: 'label', label: t('STUDIO.TEXT.LABEL', 'Tag') },
                            ]}
                        />
                        <SegmentedChoice
                            value={shot.text.font || 'serif'}
                            onChange={(font) => setText({ font })}
                            options={[
                                { value: 'serif', label: t('STUDIO.TEXT.SERIF', 'Serif') },
                                { value: 'sans', label: t('STUDIO.TEXT.SANS', 'Sans') },
                            ]}
                        />
                    </div>
                )}
            </Field>

            {(shot.kind !== 'image' && graphics.some((g) => g.type === 'text')) && (
                <Field label={t('STUDIO.SHOT.WORDS', 'Words on screen')}>
                    <div className="space-y-1.5">
                        {graphics.map((g, i) => g.type === 'text' && (
                            <div key={g.id || i} className="flex items-center gap-1.5">
                                <Input
                                    value={g.text}
                                    maxLength={160}
                                    onChange={(e) => setGraphics(graphics.map((x, j) => (j === i && x.type === 'text' ? { ...x, text: e.target.value } : x)))}
                                    className="h-9 text-sm"
                                    style={{ fontFamily: g.family ? `"${g.family}", sans-serif` : undefined, fontStyle: g.italic ? 'italic' : undefined, textTransform: g.upper ? 'uppercase' : undefined }}
                                />
                                {g.at !== undefined && <span className="w-9 shrink-0 text-end text-[11px] tabular-nums text-muted-foreground">{`${Math.round(g.at * 10) / 10}s`}</span>}
                                <Button variant="ghost" size="icon-sm" aria-label={t('STUDIO.REMOVE', 'Remove')} onClick={() => setGraphics(graphics.filter((_, j) => j !== i))}>
                                    <X className="size-3.5" />
                                </Button>
                            </div>
                        ))}
                        <p className="text-xs text-muted-foreground">{t('STUDIO.SHOT.WORDS_HINT', 'Set in the reference’s typeface, place and timing. Fine-tune them in the film editor.')}</p>
                    </div>
                </Field>
            )}

            {shot.kind === 'image' && (
                <Field label={t('STUDIO.SHOT.CALLOUTS', 'Labels on the photo')} optional>
                    <div className="space-y-1.5">
                        {graphics.map((g, i) => g.type === 'text' && (
                            <div key={g.id || i} className="flex items-center gap-1.5">
                                <Input
                                    value={g.text}
                                    maxLength={80}
                                    onChange={(e) => setGraphics(graphics.map((x, j) => (j === i && x.type === 'text' ? { ...x, text: e.target.value } : x)))}
                                    className="h-9 text-sm"
                                />
                                <Button variant="ghost" size="icon-sm" aria-label={t('STUDIO.REMOVE', 'Remove')}
                                    // A callout goes as a whole: its words, box, line and dot.
                                    onClick={() => setGraphics(graphics.filter((x, j) => (g.group ? x.group !== g.group : j !== i)))}>
                                    <X className="size-3.5" />
                                </Button>
                            </div>
                        ))}
                        {graphics.filter((g) => g.type === 'text').length < 8 && (
                            <Button variant="outline" size="sm" className="gap-1.5"
                                onClick={() => {
                                    const n = nextCalloutNumber(graphics);
                                    const y = Math.min(0.85, 0.3 + (n - 1) * 0.14);
                                    setGraphics([...graphics, ...calloutParts(n, { text: t('STUDIO.SHOT.CALLOUT_NEW', 'Label'), x: 0.55, y, labelX: 0.24, labelY: y })]);
                                }}>
                                <Plus className="size-3.5" />{t('STUDIO.SHOT.CALLOUT_ADD', 'Add a label')}
                            </Button>
                        )}
                        {graphics.length > 0 && (
                            <p className="text-xs text-muted-foreground">{t('STUDIO.SHOT.CALLOUTS_HINT', 'Drawn on the photo with a line to what they name. Move them in Design.')}</p>
                        )}
                    </div>
                </Field>
            )}
        </>
    );
};

// ── pieces ──────────────────────────────────────────────────────────

/**
 * Every version of this shot, newest first: the one on the card is outlined;
 * clicking another puts it on the card and keeps the current one here.
 */
const Versions: React.FC<{ shot: StudioShot; onUseTake: (stage: 'still' | 'clip', assetUuid: string) => void }> = ({ shot, onUseTake }) => {
    const { t } = useTranslation();
    const items: Array<{ stage: 'still' | 'clip'; assetUuid?: string; url: string; current: boolean }> = [];
    if (shot.clip?.url) items.push({ stage: 'clip', assetUuid: shot.clip.assetUuid, url: shot.clip.url, current: true });
    if (shot.still?.url) items.push({ stage: 'still', assetUuid: shot.still.assetUuid, url: shot.still.url, current: !shot.clip?.url });
    for (const x of shot.clip?.takes || []) items.push({ stage: 'clip', ...x, current: false });
    for (const x of shot.still?.takes || []) items.push({ stage: 'still', ...x, current: false });
    return (
        <div className="mt-3">
            <div className="mb-1.5 text-[11px] text-muted-foreground">{t('STUDIO.SHOT.VERSIONS', '{{n}} versions', { n: items.length })}</div>
            <div className="flex gap-1.5 overflow-x-auto pb-1">
                {items.map((v, i) => (
                    <button key={(v.assetUuid || '') + i} type="button" disabled={v.current || !v.assetUuid}
                        onClick={() => v.assetUuid && onUseTake(v.stage, v.assetUuid)}
                        title={v.current ? t('STUDIO.SHOT.ON_CARD', 'On the card') : t('STUDIO.SHOT.USE_VERSION', 'Use this version')}
                        className={cn('relative h-12 shrink-0 overflow-hidden rounded-lg border bg-muted', v.current ? SELECTED_FRAME : 'cursor-pointer border-input opacity-75 transition-opacity hover:opacity-100')}>
                        {v.stage === 'clip'
                            ? <video src={v.url} muted preload="metadata" className="h-full w-auto" />
                            : <img src={v.url} alt="" className="h-full w-auto" />}
                        {v.stage === 'clip' && <Video className="absolute bottom-1 end-1 size-3 text-white drop-shadow" />}
                    </button>
                ))}
            </div>
        </div>
    );
};

const Field: React.FC<{ label: string; optional?: boolean; children: React.ReactNode }> = ({ label, optional, children }) => {
    const { t } = useTranslation();
    return (
        <section>
            <div className="mb-2 flex items-baseline gap-1.5 text-xs font-medium text-muted-foreground">
                {label}
                {optional && <span className="font-normal opacity-70">{t('STUDIO.OPTIONAL', 'optional')}</span>}
            </div>
            {children}
        </section>
    );
};

const Ideas: React.FC<{ ideas: string[]; onPick: (v: string) => void }> = ({ ideas, onPick }) => (
    <div className="mt-2 flex flex-wrap gap-1">
        {ideas.map((idea) => (
            <button key={idea} type="button" onClick={() => onPick(idea)}
                className="max-w-full cursor-pointer truncate rounded-full border border-dashed px-2.5 py-0.5 text-start text-[11px] text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground">
                {idea}
            </button>
        ))}
    </div>
);

/** A row that opens a tool: icon in a soft square, a name and what it does. */
const ToolCard: React.FC<{
    icon: React.ReactNode; title: string; hint: string; badge?: string; aside?: React.ReactNode;
    as?: 'button' | 'div'; onClick?: () => void; large?: boolean; className?: string;
}> = ({ icon, title, hint, badge, aside, as = 'div', onClick, large, className }) => {
    const Tag: any = as;
    return (
        <Tag
            {...(as === 'button' ? { type: 'button', onClick } : {})}
            className={cn(
                'flex w-full items-center gap-3 rounded-xl border bg-background p-2.5 text-start transition-colors',
                as === 'button' && 'cursor-pointer hover:border-foreground/25 hover:bg-accent/40',
                large && 'p-3',
                className,
            )}
        >
            <span className={cn('flex shrink-0 items-center justify-center rounded-lg border bg-muted/50 text-muted-foreground [&_svg]:size-4', large ? 'size-10' : 'size-9')}>{icon}</span>
            <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5 text-sm text-foreground">
                    {title}
                    {badge && <span className="rounded-full bg-muted px-1.5 text-[10px] text-muted-foreground">{badge}</span>}
                </span>
                <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">{hint}</span>
            </span>
            {aside}
        </Tag>
    );
};

/** A photo tool as a tile in the grid: icon, name, one line, price. */
const ToolTile = React.forwardRef<HTMLButtonElement, { icon: React.ReactNode; title: string; hint: string; price?: number; onClick?: () => void } & React.ButtonHTMLAttributes<HTMLButtonElement>>(
    ({ icon, title, hint, price, onClick, ...rest }, ref) => {
        const { t } = useTranslation();
        return (
            <button ref={ref} type="button" onClick={onClick} {...rest}
                className="flex h-full cursor-pointer flex-col items-start gap-2 rounded-xl border bg-background p-2.5 text-start transition-colors hover:border-foreground/25 hover:bg-accent/40">
                <span className="flex w-full items-center justify-between">
                    <span className="flex size-8 items-center justify-center rounded-lg border bg-muted/50 text-muted-foreground [&_svg]:size-4">{icon}</span>
                    {price != null && <span className="text-[11px] text-muted-foreground">{t('STUDIO.EDIT.CREDITS', '{{n}} cr', { n: price })}</span>}
                </span>
                <span className="min-w-0">
                    <span className="block text-[13px] leading-tight text-foreground">{title}</span>
                    <span className="mt-0.5 line-clamp-2 block text-[11px] leading-snug text-muted-foreground">{hint}</span>
                </span>
            </button>
        );
    },
);
ToolTile.displayName = 'ToolTile';

/** One way to record your own camera move. */
const TakeTile: React.FC<{ icon: React.ReactNode; title: string; hint: string; onClick: () => void }> = ({ icon, title, hint, onClick }) => (
    <button type="button" onClick={onClick}
        className="flex cursor-pointer flex-col items-start gap-2 rounded-xl border border-dashed bg-background p-3 text-start transition-colors hover:border-foreground/30 hover:bg-accent/40">
        <span className="flex size-9 items-center justify-center rounded-lg border bg-muted/50 text-muted-foreground [&_svg]:size-4">{icon}</span>
        <span>
            <span className="block text-[13px] text-foreground">{title}</span>
            <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{hint}</span>
        </span>
    </button>
);
